/**
 * "Recompute figures" — the operator's way back to a computed batch.
 *
 * Recovers a batch whose create-time autopopulate ran before its telemetry had
 * finished loading (Open, with no figures) and which otherwise had no route back
 * short of accepting an upload.
 *
 * The work is four whole-period DUT reads and runs in the background, so this
 * starts a job and follows it: the stage line below the button is the only thing
 * that distinguishes a recompute in progress from one that has wedged.
 *
 * The job id lives in the URL, not in component state, so leaving the tab and
 * coming back — or reloading during a run that takes minutes — reattaches to the
 * run instead of losing it. Same reason and mechanism as the report suites.
 */

import { Alert, Button, Group, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { NotificationData } from "@mantine/notifications";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useJob, useStartRecompute } from "../../api/hooks";
import type { BatchState } from "../../api/types";
import { useHasRole } from "../../auth";
import { DisabledReasonTooltip } from "../../components/DisabledReasonTooltip";
import { ReasonModal } from "../../components/ReasonModal";
import { RunStageLine } from "../../components/RunStageLine";
import { canRecomputeBatch } from "../../lib/batchState";
import { formatApiError } from "../../lib/errors";
import { invalidateBatchData } from "../../lib/invalidate";
import {
  RECOMPUTE_RUNNING_REASON,
  jobStatusFailed,
  recomputeOutcome,
  runningJobIdFrom,
} from "../../lib/runStages";
import { useParamPatcher, useStartKey } from "./useParamPatcher";

/** URL param holding the in-flight recompute, so a reload reattaches to it. */
const JOB_PARAM = "recomputeJob";

/**
 * URL param holding the idempotency key of a start that has not answered yet.
 *
 * Parked here rather than in component state for the same reason as the job id:
 * a reload — or an unmount while the 202 is in flight — must not lose it, or the
 * retry mints a new key and starts a second DUT fan-out. Cleared the moment the
 * job id is known, since the job is the retry-safe handle from then on.
 */
const START_KEY_PARAM = "recomputeStartKey";

/** The backend `JobKind` this control is allowed to follow. */
const RECOMPUTE_KIND = "batch_recompute";

export function RecomputeButton({
  batchId,
  batchState,
}: { batchId: string; batchState: BatchState }) {
  // Recomputing rewrites the batch's filed figures; the backend refuses it for
  // read-only roles, and the button is hidden rather than shown-then-403'd.
  const hasRole = useHasRole("operations", "reviewer", "admin");
  // The backend refuses a recompute outright once the batch or its figures are
  // locked, so offering it there is offering a 409. Role and state are both
  // required, and neither is the authority — the backend is.
  const canRecompute = hasRole && canRecomputeBatch(batchState);
  const [params] = useSearchParams();
  const jobId = params.get(JOB_PARAM);
  const start = useStartRecompute();
  // Not polled when the control does not render. A bookmarked link carrying a
  // job id, opened on a locked batch or by a read-only role, would otherwise
  // poll every 2s behind an invisible button and then — because the hooks sit
  // above the render gate — announce an outcome and invalidate the batch's data
  // for someone who never asked for it.
  const job = useJob(canRecompute ? jobId : null);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  // One write path, shared with the reports tab rather than open-coded, so the
  // two surfaces that park a job handle in the URL cannot disagree about how it
  // gets written.
  const patchParams = useParamPatcher();
  const startKey = useStartKey(START_KEY_PARAM);

  const setJobId = useCallback(
    (next: string | null) => patchParams({ [JOB_PARAM]: next }),
    [patchParams],
  );

  // The job id comes out of the URL, so it can name something else entirely: a
  // bookmarked report-suite job, or a recompute for a different batch. Accepting
  // it on terminal state alone would disable this button while unrelated work
  // ran and then announce "Figures recomputed" for a batch whose figures were
  // never touched — a compliance action reported that did not happen. The
  // payload carries both fields needed to tell, so check them.
  const foreign =
    job.data != null && (job.data.kind !== RECOMPUTE_KIND || job.data.batch_id !== batchId);
  const state = job.data?.state;
  // A status read that fails *definitively* settles the run too — see
  // `jobStatusFailed`, which the report-suite tracker shares.
  const statusFailed = jobStatusFailed(job);
  const settled = state === "done" || state === "error" || statusFailed || foreign;
  const announced = useRef<string | null>(null);
  // Every job id this control has already followed. The self-id check alone
  // stops the one-hop loop; a cycle (A names B, B names A) would still spin,
  // and because each hop is served from the query cache rather than the network
  // it would spin at render speed rather than poll speed.
  const adopted = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!settled) return;
    // Keyed on the job so a remount cannot re-announce the same outcome. Falls
    // back to the URL's id when the status read is what failed — there is no
    // payload to key on then, but the id is exactly what has to be released.
    const key = job.data?.job_id ?? jobId;
    if (key === null || announced.current === key) return;
    announced.current = key;

    // Release the handle, optionally saying what happened. The `notice`-less
    // form is for an outcome that is not this batch's to report: no toast, and
    // no invalidate either, since nothing here changed.
    const release = (notice: NotificationData | null) => {
      if (notice !== null) {
        notifications.show(notice);
        void invalidateBatchData(queryClient, batchId);
      }
      setJobId(null);
    };

    // Checked BEFORE the payload: TanStack keeps the last successful `job.data`
    // when a later poll fails, so reading the cached `state` here would announce
    // "Recompute failed — the figures were not changed" when in truth only the
    // status read failed and the run may still be finishing server-side.
    if (foreign) return release(null);
    if (statusFailed || !job.data) {
      return release({
        title: "Recompute status unavailable",
        message:
          "That recompute can no longer be followed. It may already have finished, or " +
          "may still be running — check the figures below before starting another.",
        color: "orange",
        autoClose: 8000,
      });
    }

    // A terminal state is not on its own evidence that the figures moved: the
    // loser of two racing recomputes reaches `done` having recomputed nothing.
    // So the payload decides, and reporting anything comes after that.
    const done = job.data.state === "done";
    const outcome = done ? recomputeOutcome(job.data.result) : null;
    const winner = outcome?.kind === "superseded" ? outcome.runningJobId : null;
    if (winner !== null && winner !== job.data.job_id && !adopted.current.has(winner)) {
      // Follow the winner rather than settle. The run the operator asked for is
      // still happening under another id, so this stays "running": no toast, no
      // invalidate (the figures are mid-flight and would be re-read stale), and
      // the stage line carries straight over to the job actually doing the work.
      // `announced` is keyed on the job id, so adopting a new one leaves the
      // winner free to announce its own outcome.
      adopted.current.add(winner);
      setJobId(winner);
      return;
    }
    if (outcome?.kind === "superseded") {
      // Superseded with nowhere left to go — no successor named, or one already
      // followed. Said plainly: the operator's own run did not rewrite anything.
      return release({
        title: "Recompute superseded",
        message:
          "Another recompute for this batch took over, so this run changed nothing. " +
          "Check the figures below before starting another.",
        color: "orange",
        autoClose: 8000,
      });
    }
    return release(
      done
        ? {
            title: "Figures recomputed",
            message: "The workpaper below now reflects the recomputed figures.",
            color: "teal",
            autoClose: 6000,
          }
        : {
            title: "Recompute failed",
            message: job.data.last_error ?? "The figures were not changed.",
            color: "red",
            autoClose: 8000,
          },
    );
  }, [job.data, jobId, settled, statusFailed, foreign, batchId, queryClient, setJobId]);

  if (!canRecompute) return null;

  const running = jobId !== null && !settled;
  // One value behind the gate and its explanation, so they cannot drift.
  const recomputeBlocked = running ? RECOMPUTE_RUNNING_REASON : null;

  const onConfirm = async (reason: string) => {
    // Reused when one is already parked, so pressing Recompute again after a
    // start that never answered replays the first attempt's key and gets its
    // job back instead of starting a second whole-period DUT fan-out.
    const key = startKey.take();
    try {
      const started = await start.mutateAsync({ batchId, reason, idempotencyKey: key });
      patchParams({ ...startKey.release(), [JOB_PARAM]: started.job_id });
      setOpen(false);
    } catch (err) {
      // Already running. Nothing went wrong and there is nothing to retry — the
      // work is happening, so attach to it rather than reporting an error.
      const already = runningJobIdFrom(err);
      if (already !== null) {
        patchParams({ ...startKey.release(), [JOB_PARAM]: already });
        // Adopting is a success, so the handled 409 must not stay on the
        // mutation: it would resurface the moment the modal is reopened for the
        // next legitimate run and read as a refusal that is not happening.
        start.reset();
        setOpen(false);
        notifications.show({
          title: "Recompute already running",
          message: "A recompute is already in progress for this batch — showing that one.",
          color: "blue",
          autoClose: 5000,
        });
        return;
      }
      // A locked batch (409) or an unknown one (404) surfaces inline in the
      // modal. The start key is deliberately kept: a retry of this same intent
      // must carry the same key, and it is released when one finally lands.
    }
  };

  return (
    <Stack gap="xs">
      <Group justify="flex-end">
        {/* Above the button, not below: the stage line this tooltip explains sits
            directly underneath, and covering it while the operator reads why
            hides the answer to their next question. */}
        <DisabledReasonTooltip reason={recomputeBlocked} position="top-end">
          <Button
            data-tour="recompute"
            size="xs"
            variant="default"
            leftSection={<IconRefresh size={14} />}
            onClick={() => setOpen(true)}
            loading={start.isPending}
            disabled={recomputeBlocked !== null}
          >
            Recompute figures
          </Button>
        </DisabledReasonTooltip>
      </Group>
      {running ? (
        <Group justify="flex-end">
          <RunStageLine
            label="Recompute running…"
            stage={job.data?.stage}
            data-test="recompute-running"
          />
        </Group>
      ) : null}
      {job.isError && !statusFailed ? (
        // Transient only — a definitive failure clears the run above instead of
        // leaving advice on screen that cannot work.
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">
            Couldn't read the recompute's progress ({formatApiError(job.error)}) — retrying. The run
            itself is unaffected.
          </Text>
        </Alert>
      ) : null}
      <ReasonModal
        opened={open}
        title="Recompute figures"
        confirmLabel="Recompute"
        busy={start.isPending}
        error={start.error}
        onConfirm={onConfirm}
        onClose={() => {
          setOpen(false);
          start.reset();
        }}
      />
    </Stack>
  );
}
